/**
 * Per-page layout persistence. Each page's panels live in localStorage under
 * their own key; the project (page list) lives separately in ProjectEditor.
 */

const STORAGE_KEY_PREFIX = "striply_layout_";

export const pageStorageKey = (pageId) => `${STORAGE_KEY_PREFIX}${pageId || "default"}`;

// Editors register a flush for their debounced save so callers that read
// page data directly (duplicate, page summaries) always see the latest edits.
const pendingSaveFlushers = new Set();

export const registerPendingSaveFlusher = (flush) => {
  pendingSaveFlushers.add(flush);
  return () => pendingSaveFlushers.delete(flush);
};

export const flushPendingPageSaves = () => {
  pendingSaveFlushers.forEach((flush) => flush());
};

export const copyPageData = (fromPageId, toPageId) => {
  flushPendingPageSaves();
  try {
    const saved = localStorage.getItem(pageStorageKey(fromPageId));
    if (saved) localStorage.setItem(pageStorageKey(toPageId), saved);
  } catch (e) {
    console.warn("Failed to copy page data:", e);
  }
};

export const deletePageData = (pageId) => {
  try {
    localStorage.removeItem(pageStorageKey(pageId));
  } catch (e) {
    console.warn("Failed to delete page data:", e);
  }
};

/** Panel count for a page from its saved layout, or null if never saved. */
export const readPagePanelCount = (pageId) => {
  try {
    const saved = localStorage.getItem(pageStorageKey(pageId));
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    return Array.isArray(parsed.cells) ? parsed.cells.length : null;
  } catch (e) {
    return null;
  }
};

/** A page's saved layout ({ cells, pageSizeKey, comicStyleKey, … }), or null. */
export const readPageData = (pageId) => {
  try {
    const saved = localStorage.getItem(pageStorageKey(pageId));
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    return Array.isArray(parsed.cells) ? parsed : null;
  } catch (e) {
    return null;
  }
};
